import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "@/components/AuthProvider";
import {
  LayoutDashboard, Play, Layers, Image, Users, Megaphone,
  ScrollText, Settings, Wand2, Tags, ShieldCheck, RefreshCw,
  LogOut, User, Rocket, Terminal, BookOpen, Radio, Inbox, Building2, RouteOff,
} from "lucide-react";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/processor", icon: Play, label: "Test Processor" },
  { to: "/deploy", icon: Rocket, label: "Deploy Signature" },
  { to: "/exchange-setup", icon: Terminal, label: "Exchange Setup" },
  { to: "/relay", icon: Radio, label: "Relay Control" },
  { to: "/outbox", icon: Inbox, label: "Outbox" },
  { to: "/bypass", icon: RouteOff, label: "Delivery Bypass" },
  null, // separator
  { to: "/templates", icon: Layers, label: "Templates" },
  { to: "/builder", icon: Wand2, label: "Template Builder" },
  { to: "/brands", icon: Tags, label: "Brand Assignment" },
  { to: "/assignments", icon: Building2, label: "Department Templates" },
  { to: "/rules", icon: ShieldCheck, label: "Rules & Exceptions" },
  null,
  { to: "/sync", icon: RefreshCw, label: "Directory Sync" },
  { to: "/staff", icon: Users, label: "Staff Directory" },
  { to: "/assets", icon: Image, label: "Assets" },
  { to: "/campaigns", icon: Megaphone, label: "Campaigns" },
  null,
  { to: "/audit-log", icon: ScrollText, label: "Audit Log" },
  { to: "/settings", icon: Settings, label: "Settings" },
  null,
  { to: "/help", icon: BookOpen, label: "Help & Setup Guide" },
];

export default function Layout() {
  const { user, logout, authConfig } = useAuth();

  return (
    <div className="flex h-screen bg-[#FAFAFA] font-body" data-testid="app-layout">
      <aside className="w-60 bg-white border-r border-[#E1DFDD] flex flex-col shrink-0" data-testid="sidebar">
        <div className="px-5 py-4 border-b border-[#E1DFDD]">
          <h1 className="font-heading text-lg font-semibold tracking-tight text-[#0F6CBD]">
            SignatureForge
          </h1>
          <p className="text-[11px] text-[#5E5E5E] mt-0.5 tracking-wide">M365 Signature Platform</p>
        </div>
        <nav className="flex-1 py-1 overflow-y-auto" data-testid="sidebar-nav">
          {navItems.map((item, idx) =>
            item === null ? (
              <div key={`sep-${idx}`} className="my-1 mx-4 border-t border-[#E1DFDD]" />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-5 py-2 text-sm transition-colors duration-150 ${
                    isActive
                      ? "bg-[#EBF3FC] text-[#0F6CBD] font-medium border-l-[3px] border-[#0F6CBD]"
                      : "text-[#5E5E5E] hover:bg-[#F3F2F1] hover:text-[#171717] border-l-[3px] border-transparent"
                  }`
                }
              >
                <item.icon size={17} strokeWidth={1.8} />
                <span>{item.label}</span>
              </NavLink>
            )
          )}
        </nav>
        {/* User info footer */}
        <div className="px-4 py-3 border-t border-[#E1DFDD]">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-[#0F6CBD] flex items-center justify-center shrink-0">
              <User size={14} className="text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-[#171717] truncate">{user?.name || "Admin"}</p>
              <p className="text-[10px] text-[#5E5E5E] truncate">{user?.email}</p>
            </div>
            <button onClick={logout} className="text-[#5E5E5E] hover:text-[#A4262C] shrink-0" data-testid="logout-btn" title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
          {authConfig?.dev_bypass && (
            <p className="text-[9px] text-[#D83B01] mt-1 uppercase tracking-wider font-semibold">Dev Bypass Active</p>
          )}
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto" data-testid="main-content">
        <Outlet />
      </main>
    </div>
  );
}
